import { z } from 'zod'
import { MatchSchema } from '~/types/match'

const viteMode = import.meta.env.MODE
const API_BASE_URL = viteMode === 'development'
  ? 'http://localhost:3000/api/v1'
  : 'https://api.padlo.co/api/v1'
const DEFAULT_SUBDOMAIN = 'padelkeskus'

export function getSubdomain(): string {
  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return DEFAULT_SUBDOMAIN
  }

  // For production, extract subdomain
  const parts = hostname.split('.')
  if (parts.length > 2) {
    return parts[0]
  }

  return DEFAULT_SUBDOMAIN
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message)
  }
}

const SUBDOMAIN_HEADER = {
  'X-Subdomain': getSubdomain()
}

export async function fetchApi<T>(
  endpoint: string,
  schema: z.ZodType<T>,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...SUBDOMAIN_HEADER,
    },
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(
      data.message || 'An error occurred',
      response.status,
      data
    )
  }

  const parsedData = schema.safeParse(data)

  if (!parsedData.success) {
    console.error(parsedData.error)
    console.log(data);
    throw new ApiError('Invalid data', 400, parsedData.error)
  }

  return parsedData.data
}

export async function fetchApiDelete(
  endpoint: string,
  options: RequestInit = {}
): Promise<boolean> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...SUBDOMAIN_HEADER,
    },
  })

  if (!response.ok) {
    let errorMessage = 'An error occurred';
    try {
      const data = await response.json()
      const jsonErrors = data.errors ? data.errors.join(', ') : data.error;
      if (jsonErrors) errorMessage = jsonErrors;
    } catch (error) {
      console.error(error)
    }
    throw new ApiError(errorMessage, response.status)
  }

  return true
}

// Match-specific API functions
export async function fetchMatches() {
  return fetchApi('/matches', z.array(MatchSchema))
}

export async function fetchMatch(id: number) {
  return fetchApi(`/matches/${id}`, MatchSchema)
}
